import React from 'react';
import { PageType } from '../App';
import UseCaseMMC from '../components/UseCaseMMC';
import HowItWorksSection from '../components/HowItWorksSection';

interface InvestorPageProps {
  setCurrentPage: (page: PageType) => void;
}

const InvestorPage: React.FC<InvestorPageProps> = ({ setCurrentPage }) => {
  return (
    <>
      <section className="uc-hero">
        <div className="uc-hero-inner">
          <span className="uc-hero-label">For PE &amp; VC Investors</span>
          <h1>Your portfolio company wants to expand.<br/><em>Simulate it before you fund it.</em></h1>
          <p>Most expansion plans are built on assumptions. We give you an independent simulation of true costs, projected month by month revenue build-up, and realistic risk scenarios, so you can see what the entry will most likely look like before you deploy a single euro.</p>
          <a href="#" onClick={(e) => { e.preventDefault(); setCurrentPage('home'); }} className="uc-hero-cta">Start Your Free Simulation</a>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',marginTop:'20px'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:'10px',padding:'12px 16px',background:'rgba(255,255,255,0.05)',borderRadius:'10px',border:'1px solid rgba(255,255,255,0.1)',maxWidth:'400px'}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7c9a6e" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',lineHeight:'1.5',margin:0}}><strong style={{color:'rgba(255,255,255,0.7)',fontWeight:600}}>Your data stays private.</strong> Strictly confidential. Never shared without your permission.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="uc-section">
        <div className="uc-label"><span>Why Investment Teams Use Market Entry</span></div>
        <h2 className="uc-title">Don't rely on the founder's pitch deck.<br/><em>Run your own simulation.</em></h2>
        <p className="uc-sub">Every expansion pitch comes with a plan. Most are built on optimism. We give you an independent, data-driven simulation so you can make faster, sharper investment decisions — and back the right expansions with confidence.</p>
        <div className="uc-features">
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
            <h3>Make better investment decisions, faster</h3>
            <p>Getting a clear picture of an expansion market typically takes weeks of internal research. We deliver a complete simulation in 72 hours so you have the data you need well before any commitment is made.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></div>
            <h3>Protect your portfolio from costly market mistakes</h3>
            <p>The wrong market choice can set a company back 12 to 18 months. We automatically surface better-performing alternatives so your portfolio companies enter where the conditions are right, not just where they assumed.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></div>
            <h3>Save weeks of due diligence time</h3>
            <p>Run expansion simulations across every deal and portfolio company without internal resource. Each simulation is independent, built on real market data, delivered in 72 hours, and completely free, so your team spends time deciding, not researching.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg></div>
            <h3>Walk into every board meeting with independent data</h3>
            <p>A full 12-month simulation with go/no-go verdict, cost breakdown, projected cashflow, and multi-market comparison gives you the data to challenge, support, or redirect an expansion plan with confidence.</p>
          </div>
        </div>
      </div>

      <UseCaseMMC />
      <HowItWorksSection type="investor" />
    </>
  );
};

export default InvestorPage;
